import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import trTR from 'antd/locale/tr_TR';
import enUS from 'antd/locale/en_US';
import { useTranslation } from 'react-i18next';
import Login from './components/Login';
import Register from './components/Register';
import Home from './components/Home';
import Dashboard from './components/Dashboard';
import Stations from './components/Stations';
import Campaigns from './components/Campaigns';
import SubUsers from './components/SubUsers';
import StationMessages from './components/StationMessages';
import Analytics from './components/Analytics';
import CompetitionAnalysis from './components/CompetitionAnalysis';
import APIDocumentation from './components/APIDocumentation';
import BackupRestore from './components/BackupRestore';
import PaymentSubscription from './components/PaymentSubscription';
import MobileIntegration from './components/MobileIntegration';
import Financial from './components/Financial';
import FuelPrices from './components/FuelPrices';
import EVChargingPrices from './components/EVChargingPrices';
import Stocks from './components/Stocks';
import Transactions from './components/Transactions';
import StationManagement from './components/StationManagement';
import Employees from './components/Employees';
import Notifications from './components/Notifications';
import Alerts from './components/Alerts';
import Customers from './components/Customers';
import Invoices from './components/Invoices';
import Suppliers from './components/Suppliers';
import Inventory from './components/Inventory';
import Complaints from './components/Complaints';
import Surveys from './components/Surveys';
import ScheduledReports from './components/ScheduledReports';
import BulkOperations from './components/BulkOperations';
import ActivityLogs from './components/ActivityLogs';
import NotificationService from './components/NotificationService';
import Profile from './components/Profile';
import Settings from './components/Settings';
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, stationOwner, subUser, userType, loading } = useAuth();
  
  // Loading durumunda bekle
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Yükleniyor...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Ana kullanıcılar için aktif kontrolü (isVerified kontrolünü kaldırdık - admin onayı bekleyebilir)
  if (userType === 'owner' && stationOwner && !stationOwner.isActive) {
    return <Navigate to="/" />;
  }
  
  // Alt kullanıcılar için aktif kontrolü
  if (userType === 'subuser' && subUser && !subUser.isActive) {
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isDark } = useTheme();
  const { i18n } = useTranslation();
  
  // Ant Design locale'i i18n diline göre ayarla
  const antdLocale = i18n.language === 'en' ? enUS : trTR;
  
  return (
    <ConfigProvider 
      locale={antdLocale}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
        },
      }}
    >
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="App">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/panel" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="stations" element={<Stations />} />
                <Route path="campaigns" element={<Campaigns />} />
                <Route path="subusers" element={<SubUsers />} />
                <Route path="messages" element={<StationMessages />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="competition-analysis" element={<CompetitionAnalysis />} />
                <Route path="api-docs" element={<APIDocumentation />} />
                <Route path="backup-restore" element={<BackupRestore />} />
                <Route path="payment-subscription" element={<PaymentSubscription />} />
                <Route path="mobile-integration" element={<MobileIntegration />} />
                <Route path="financial" element={<Financial />} />
                <Route path="fuel-prices" element={<FuelPrices />} />
                <Route path="ev-charging-prices" element={<EVChargingPrices />} />
                <Route path="stocks" element={<Stocks />} />
                <Route path="transactions" element={<Transactions />} />
                <Route path="station-management" element={<StationManagement />} />
                <Route path="employees" element={<Employees />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="alerts" element={<Alerts />} />
                <Route path="customers" element={<Customers />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="suppliers" element={<Suppliers />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="complaints" element={<Complaints />} />
                <Route path="surveys" element={<Surveys />} />
                <Route path="scheduled-reports" element={<ScheduledReports />} />
                <Route path="bulk-operations" element={<BulkOperations />} />
                <Route path="activity-logs" element={<ActivityLogs />} />
                <Route path="notification-service" element={<NotificationService />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
              </Route>
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </LanguageProvider>
  );
};

export default App;